import {
	campaignInterviewSettingsShowQueryKey,
	useCampaignInterviewSettingsShow,
	useCampaignInterviewSettingsUpdate,
} from '@jobilla/nexus-sdk'
import type { CampaignInterviewSettingsShowResponse, CampaignInterviewSettingsUpdateRequest } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

const DEFAULT_FORM: CampaignInterviewSettingsUpdateRequest = {
	interviewDurationMinutes: null,
	interviewBookingWindowDays: null,
	interviewTokenExpiryDays: null,
	interviewMeetingLocationType: null,
	interviewMeetingLocationDetails: null,
	interviewDescription: null,
	interviewBookedStageId: null,
	interviewBookedStageType: null,
}

function seedForm(
	data: CampaignInterviewSettingsShowResponse | null | undefined,
): CampaignInterviewSettingsUpdateRequest {
	if (data == null) {
		return DEFAULT_FORM
	}
	return {
		interviewDurationMinutes: data.interviewDurationMinutes,
		interviewBookingWindowDays: data.interviewBookingWindowDays,
		interviewTokenExpiryDays: data.interviewTokenExpiryDays,
		interviewMeetingLocationType: data.interviewMeetingLocationType as CampaignInterviewSettingsUpdateRequest[
			'interviewMeetingLocationType'
		],
		interviewMeetingLocationDetails: data.interviewMeetingLocationDetails,
		interviewDescription: data.interviewDescription,
		interviewBookedStageId: data.interviewBookedStageId,
		interviewBookedStageType: data.interviewBookedStageType,
	}
}

export function useInterviewSettingsForm(campaignId: string) {
	const queryClient = useQueryClient()
	const { data: serverData, isLoading, isError: isLoadError } = useCampaignInterviewSettingsShow(campaignId)
	const mutation = useCampaignInterviewSettingsUpdate()

	const [form, setForm] = useState<CampaignInterviewSettingsUpdateRequest>(DEFAULT_FORM)
	const [isDirty, setIsDirty] = useState(false)
	const [savedRecently, setSavedRecently] = useState(false)

	useEffect(() => {
		if (serverData != null && !isDirty) {
			setForm(seedForm(serverData))
		}
	}, [serverData, isDirty])

	function updateForm(
		updater: (prev: CampaignInterviewSettingsUpdateRequest) => CampaignInterviewSettingsUpdateRequest,
	): void {
		setForm((prev) => updater(prev))
		setIsDirty(true)
		setSavedRecently(false)
	}

	function handleSave(): void {
		mutation.mutate(
			{ campaign: campaignId, data: form },
			{
				onSuccess: (data) => {
					setForm(seedForm(data))
					queryClient.setQueryData(campaignInterviewSettingsShowQueryKey(campaignId), data)
					setIsDirty(false)
					setSavedRecently(true)
					setTimeout(() => setSavedRecently(false), 2500)
				},
			},
		)
	}

	const errorData = mutation.error?.response?.data
	const validationErrors = errorData && 'errors' in errorData ? errorData.errors : {}
	const hasValidationErrors = Object.keys(validationErrors).length > 0
	const validationError = hasValidationErrors ? errorData?.message ?? null : null
	const hasGenericError = mutation.isError && !hasValidationErrors

	return {
		form,
		isLoading,
		isLoadError,
		isDirty,
		savedRecently,
		isPending: mutation.isPending,
		validationError,
		hasGenericError,
		updateForm,
		handleSave,
	}
}
